import React, { useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { PageLayout } from '@/shared/layout/PageLayout';
import {
  Button,
  Card,
  Form,
  FormGroup,
  FormLabel,
  FormError,
  Input,
  LoadingState,
  ErrorState
} from '@/components/ui';
import { useCompanies, useCompanyDetail } from './hooks/useCompanies';
import type { Company } from './types';

const companySchema = z.object({
  name: z.string().min(2, 'Tên công ty phải có ít nhất 2 ký tự'),
  code: z.string().min(2, 'Mã công ty phải có ít nhất 2 ký tự').toUpperCase(),
  email: z.string().email('Email không hợp lệ').optional().or(z.literal('')),
  phone: z.string().optional().or(z.literal('')),
  address: z.string().optional().or(z.literal('')),
  city: z.string().optional().or(z.literal('')),
  website: z.string().url('Website không hợp lệ').optional().or(z.literal('')),
  subscription_plan_code: z.enum(['free', 'basic', 'pro', 'enterprise']),
  is_active: z.boolean(),
  max_projects: z.number().min(0),
  max_users: z.number().min(0),
  max_assets: z.number().min(0),
  description: z.string().optional().or(z.literal('')),
});

type CompanyFormValues = z.infer<typeof companySchema>;

export default function CompanyEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { company, isLoading: isLoadingDetail, error: detailError } = useCompanyDetail(id);
  const { upsertCompany, isSaving } = useCompanies();

  const { register, handleSubmit, formState: { errors }, reset } = useForm<CompanyFormValues>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      subscription_plan_code: 'free',
      is_active: true,
      max_projects: 3,
      max_users: 10,
      max_assets: 100,
    }
  });

  useEffect(() => {
    if (company) {
      reset({
        name: company.name,
        code: company.code,
        email: company.email || '',
        phone: company.phone || '',
        address: company.address || '',
        city: company.city || '',
        website: company.website || '',
        subscription_plan_code: company.subscription_plan_code,
        is_active: company.is_active,
        max_projects: company.max_projects,
        max_users: company.max_users,
        max_assets: company.max_assets,
        description: company.description || '',
      });
    }
  }, [company, reset]);

  const onSubmit = async (data: CompanyFormValues) => {
    if (!id) return;
    const res = await upsertCompany({ ...data, id });
    if (!res.error) {
      navigate(`/companies/detail/${id}`);
    }
  };

  if (isLoadingDetail) return <LoadingState />;
  if (detailError) return <ErrorState message={detailError.message} />;
  if (!company) return <ErrorState message="Không tìm thấy thông tin công ty" />;

  return (
    <PageLayout
      title={`Chỉnh sửa: ${company.name}`}
      description="Cập nhật thông tin tổ chức, cấu hình gói dịch vụ và hạn mức tài nguyên."
      breadcrumbs={[
        { label: 'Hệ thống', path: '/admin' },
        { label: 'Công ty', path: '/companies' },
        { label: company.name, path: `/companies/detail/${id}` },
        { label: 'Chỉnh sửa' },
      ]}
    >
      <Form onSubmit={handleSubmit(onSubmit)} className="max-w-4xl space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="p-6 space-y-6">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <i className="ri-building-line text-green-600" />
              Thông tin định danh
            </h3>

            <FormGroup>
              <FormLabel required>Tên công ty</FormLabel>
              <Input
                {...register('name')}
                error={errors.name?.message}
                placeholder="VD: Landscape JSC"
              />
            </FormGroup>

            <FormGroup>
              <FormLabel required>Mã code (Tenant ID)</FormLabel>
              <Input
                {...register('code')}
                error={errors.code?.message}
                placeholder="VD: LANDSCAPE"
                disabled // Mã định danh thường không cho phép sửa sau khi tạo
                className="font-mono bg-slate-50 cursor-not-allowed"
              />
              <p className="text-[10px] text-slate-400 mt-1 italic">
                * Mã định danh được dùng cho URL và phân tách dữ liệu (Multi-tenant ID). Không thể thay đổi.
              </p>
            </FormGroup>

            <FormGroup>
              <FormLabel>Mô tả tổ chức</FormLabel>
              <textarea
                {...register('description')}
                rows={4}
                className="w-full p-4 rounded-2xl border border-slate-200 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none transition-all text-sm"
                placeholder="Giới thiệu ngắn về công ty..."
              />
              <FormError>{errors.description?.message}</FormError>
            </FormGroup>
          </Card>

          <div className="space-y-6">
            <Card className="p-6 space-y-6">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <i className="ri-contacts-line text-green-600" />
                Thông tin liên hệ
              </h3>

              <FormGroup>
                <FormLabel>Email hệ thống</FormLabel>
                <Input
                  {...register('email')}
                  error={errors.email?.message}
                  placeholder="contact@company.com"
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Số điện thoại</FormLabel>
                <Input
                  {...register('phone')}
                  error={errors.phone?.message}
                  placeholder="090..."
                />
              </FormGroup>

              <FormGroup>
                <FormLabel>Website</FormLabel>
                <Input
                  {...register('website')}
                  error={errors.website?.message}
                  placeholder="https://..."
                />
              </FormGroup>
            </Card>

            <Card className="p-6 space-y-6">
              <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
                <i className="ri-vip-crown-line text-green-600" />
                Gói dịch vụ & Trạng thái
              </h3>

              <FormGroup>
                <FormLabel>Gói đăng ký</FormLabel>
                <select
                  {...register('subscription_plan_code')}
                  className="w-full h-12 px-4 rounded-2xl border border-slate-200 focus:border-green-500 focus:ring-2 focus:ring-green-500/20 outline-none transition-all bg-white font-semibold"
                >
                  <option value="free">Free Edition</option>
                  <option value="basic">Basic Edition</option>
                  <option value="pro">Pro Edition</option>
                  <option value="enterprise">Enterprise Edition</option>
                </select>
                <FormError>{errors.subscription_plan_code?.message}</FormError>
              </FormGroup>

              <div className="flex items-center gap-3 p-4 bg-slate-50 rounded-2xl border border-slate-100">
                <input
                  type="checkbox"
                  {...register('is_active')}
                  id="is_active_toggle"
                  className="w-6 h-6 rounded-lg border-slate-300 text-green-600 focus:ring-green-500 cursor-pointer"
                />
                <label htmlFor="is_active_toggle" className="text-sm font-bold text-slate-700 cursor-pointer select-none">
                  Kích hoạt tài khoản người dùng
                </label>
              </div>
            </Card>
          </div>
        </div>

        <Card className="p-6 space-y-6">
          <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
            <i className="ri-hard-drive-2-line text-green-600" />
            Cấu hình hạn mức tài nguyên (Quotas)
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FormGroup>
              <FormLabel>Số dự án tối đa</FormLabel>
              <Input
                type="number"
                {...register('max_projects', { valueAsNumber: true })}
                error={errors.max_projects?.message}
                className="text-lg font-bold text-center h-14"
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>Số nhân sự tối đa</FormLabel>
              <Input
                type="number"
                {...register('max_users', { valueAsNumber: true })}
                error={errors.max_users?.message}
                className="text-lg font-bold text-center h-14"
              />
            </FormGroup>
            <FormGroup>
              <FormLabel>Số tài sản tối đa</FormLabel>
              <Input
                type="number"
                {...register('max_assets', { valueAsNumber: true })}
                error={errors.max_assets?.message}
                className="text-lg font-bold text-center h-14"
              />
            </FormGroup>
          </div>
        </Card>

        <div className="flex items-center justify-end gap-4 pt-6 border-t border-slate-100">
          <Button
            variant="ghost"
            className="h-12 px-8"
            onClick={() => navigate(`/companies/detail/${id}`)}
            type="button"
          >
            Hủy bỏ
          </Button>
          <Button
            variant="primary"
            className="h-12 px-12 shadow-lg shadow-green-500/20"
            isLoading={isSaving}
            type="submit"
            icon="ri-save-line"
          >
            Lưu thay đổi
          </Button>
        </div>
      </Form>
    </PageLayout>
  );
}
